// frontend/src/pages/ProfilePage.tsx - UPDATED FOR HOOKS/STORE INTEGRATION
// MODIFIED FUNCTION: Updated to use new API hooks, store, and utils following the pattern of CorrespondencesPage, ResponsesPage, and ProjectsPage

import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, debounce } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';

// UPDATED: Use hooks following new architecture
import { 
  useUser,
  useUpdateUser,
  useResetUserPassword 
} from '@/hooks/useUsers';

// UPDATED: Use API types instead of local interfaces
import type {
  User,
  UserUpdateRequest,
  PasswordResetRequest,
  UserRole,
  NormalizedUser
} from '@/api/types';

// ===== LOCAL INTERFACES =====

// Profile form data interface
interface ProfileFormData {
  username: string;
  department: string;
  email: string;
}

// Password change data interface
interface PasswordChangeData {
  newPassword: string;
  confirmPassword: string;
  generatePassword: boolean;
}

// ===== UTILITY COMPONENTS =====

const ProfileAvatar: React.FC<{
  username?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}> = memo(({ username = '', size = 'lg', className }) => {
  const initials = username.charAt(0).toUpperCase() || '?';
  
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-24 h-24 text-2xl',
    xl: 'w-32 h-32 text-4xl'
  };
  
  return (
    <div className={cn(
      'bg-blue-600 rounded-full flex items-center justify-center',
      sizeClasses[size],
      className
    )}>
      <span className="text-white font-bold">
        {initials}
      </span>
    </div>
  );
});

const RoleBadge: React.FC<{
  role: UserRole;
  className?: string;
}> = memo(({ role, className }) => {
  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'implementor': 
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'admin': 
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'contributor': 
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      default: 
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  return (
    <span className={cn(
      'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
      getRoleColor(role),
      className
    )}>
      {role}
    </span>
  );
});

// ===== ERROR BOUNDARY =====

class ProfileErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ProfilePage Error Boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Profile Error</h2>
          <p className="text-gray-600 mb-4">Something went wrong loading your profile.</p>
          <Button onClick={() => this.setState({ hasError: false })}>
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== MAIN COMPONENT =====

export const ProfilePage: React.FC = () => {
  // ===== HOOKS =====
  
  const { user: currentUser, isAuthenticated, isLoading: authLoading, fetchUser } = useAuthStore();
  const { setGlobalError, clearGlobalError, addNotification } = useAppStore();

  // UPDATED: Use new hooks following the pattern
  const {
    data: profileUserData,
    loading: profileLoading,
    error: profileError,
    refetch: refetchProfile
  } = useUser(currentUser?.id, {
    immediate: !!currentUser?.id
  });

  const {
    mutate: updateProfile,
    loading: updateLoading,
    error: updateError
  } = useUpdateUser({
    onSuccess: (updatedUser) => {
      setSuccessMessage('Profile updated successfully');
      refetchProfile();
      fetchUser(); // Use fetchUser instead of refetchCurrentUser
      setHasChanges(false);
    },
    onError: (error) => {
      setGlobalError(error.message || 'Failed to update profile');
    }
  });

  const {
    mutate: resetPassword,
    loading: passwordLoading,
    error: passwordError
  } = useResetUserPassword({
    onSuccess: (response: any) => {
      setSuccessMessage('Password reset successfully');
      setPasswordData({ 
        newPassword: '', 
        confirmPassword: '', 
        generatePassword: false 
      });
      setPasswordErrors({});
      
      // Show generated password if applicable
      if (response.temporaryPassword) {
        addNotification({ type: 'info', title: 'New Password Generated', message: `Your new password is: ${response.temporaryPassword}. Please save this password securely and change it after logging in.` });
      }
    },
    onError: (error) => {
      setGlobalError(error.message || 'Failed to reset password');
    }
  });

  // ===== LOCAL STATE =====

  const [profileData, setProfileData] = useState<ProfileFormData>({
    username: '',
    department: '',
    email: ''
  });

  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    newPassword: '',
    confirmPassword: '',
    generatePassword: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'account'>('profile');

  // ===== EFFECTS =====

  // Initialize profile data from user
  useEffect(() => {
    const user = profileUserData || currentUser;
    if (user) {
      setProfileData({
        username: user.username || '',
        department: user.department || '',
        email: user.email || ''
      });
    }
  }, [profileUserData, currentUser]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Clear errors when component mounts
  useEffect(() => {
    return () => clearGlobalError();
  }, [clearGlobalError]);

  // ===== HANDLERS =====

  const handleProfileChange = useCallback((field: keyof ProfileFormData, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    
    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => {
        const { [field]: removed, ...rest } = prev;
        return rest;
      });
    }
  }, [errors]);

  const handlePasswordChange = useCallback((field: keyof PasswordChangeData, value: string | boolean) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field-specific errors
    if (passwordErrors[field]) {
      setPasswordErrors(prev => {
        const { [field]: removed, ...rest } = prev;
        return rest;
      });
    }
  }, [passwordErrors]);

  // Validation functions
  const validateProfile = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!profileData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else if (profileData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(profileData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, dots, underscores, and hyphens';
    }

    if (profileData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [profileData]);

  const validatePassword = useCallback((): boolean => {
    // Since the API only supports generating passwords automatically,
    // we don't need to validate manual password input
    // This validation is kept for future enhancement
    return true;
  }, []);

  // Debounced save function
  const debouncedSave = useMemo(
    () => debounce(() => {
      if (hasChanges && validateProfile() && currentUser?.id) {
        const updateData: UserUpdateRequest = {
          username: profileData.username.trim(),
          department: profileData.department.trim() || undefined,
          email: profileData.email.trim() || undefined
        };
        
        updateProfile({ id: currentUser.id, data: updateData });
      }
    }, 1000),
    [hasChanges, profileData, validateProfile, currentUser?.id, updateProfile]
  );

  const handleProfileSave = useCallback(() => {
    if (!currentUser?.id) {
      setGlobalError('No user data available');
      return;
    }

    if (!validateProfile()) {
      return;
    }

    clearGlobalError();
    
    const updateData: UserUpdateRequest = {
      username: profileData.username.trim(),
      department: profileData.department.trim() || undefined,
      email: profileData.email.trim() || undefined
    };
    
    updateProfile({ id: currentUser.id, data: updateData });
  }, [currentUser?.id, validateProfile, clearGlobalError, profileData, updateProfile]);

  const handlePasswordReset = useCallback(() => {
    if (!currentUser?.id) {
      setGlobalError('No user data available');
      return;
    }

    if (!validatePassword()) {
      return;
    }

    clearGlobalError();

    // The useResetUserPassword hook only expects userId - it generates a password automatically
    resetPassword(currentUser.id);
  }, [currentUser?.id, validatePassword, clearGlobalError, resetPassword]);

  // ===== COMPUTED VALUES =====

  const isLoading = authLoading || profileLoading;
  const isSaving = updateLoading || passwordLoading;
  const hasErrors = profileError || updateError || passwordError;
  const user = profileUserData || currentUser;

  // Organization name helper
  const organizationName = useMemo(() => {
    if (!user?.organization) return 'Unknown';
    if (typeof user.organization === 'string') return user.organization;
    return user.organization.name || 'Unknown';
  }, [user?.organization]);

  // ===== RENDER =====

  // Loading state
  if (isLoading) {
    return (
      <PageLayout title="Profile" subtitle="Manage your personal information and account settings">
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  // Not authenticated
  if (!isAuthenticated || !user) {
    return (
      <PageLayout title="Profile" subtitle="Manage your personal information and account settings">
        <ErrorAlert message="Please log in to view your profile." />
      </PageLayout>
    );
  }

  return (
    <ProfileErrorBoundary>
      <PageLayout 
        title="Profile" 
        subtitle="Manage your personal information and account settings"
      >
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 rounded-md p-4 flex items-center justify-between">
              <span>{successMessage}</span>
              <button 
                onClick={() => setSuccessMessage(null)}
                className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                aria-label="Dismiss success message"
              >
                ×
              </button>
            </div>
          )}

          {/* Error Display */}
          {hasErrors && (
            <ErrorAlert
              message="There was an error with your profile. Please try again."
              onClose={clearGlobalError}
            />
          )}

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8" role="tablist" aria-label="Profile settings">
              {[
                { key: 'profile', label: 'Profile', icon: '👤' },
                { key: 'password', label: 'Password', icon: '🔐' },
                { key: 'account', label: 'Account Info', icon: '📋' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  role="tab"
                  id={`tab-${tab.key}`}
                  aria-selected={activeTab === tab.key}
                  aria-controls={`tabpanel-${tab.key}`}
                  onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  className={cn(
                    'flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm',
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Photo Sidebar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="text-center">
                <ProfileAvatar 
                  username={user.username} 
                  size="xl"
                  className="mx-auto mb-4" 
                />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  {user.username}
                </h3>
                <div className="mb-2">
                  <RoleBadge role={user.role} />
                </div>
                {user.department && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    {user.department}
                  </p>
                )}
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  <div>Member since {formatDate(user.createdAt)}</div>
                  {user.lastLoginAt && (
                    <div>Last login {formatDate(user.lastLoginAt)}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {activeTab === 'profile' && (
                <div role="tabpanel" id="tabpanel-profile" aria-labelledby="tab-profile" className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Profile Information
                  </h2>
                  
                  <form onSubmit={(e) => { e.preventDefault(); handleProfileSave(); }} className="space-y-4">
                    <div>
                      <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Username *
                      </label>
                      <Input
                        id="username"
                        type="text"
                        value={profileData.username}
                        onChange={(e) => handleProfileChange('username', e.target.value)}
                        className={errors.username ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                        placeholder="Enter your username"
                        required
                      />
                      {errors.username && (
                        <p className="text-red-500 text-xs mt-1">{errors.username}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                      </label>
                      <Input
                        id="email"
                        type="email"
                        value={profileData.email}
                        onChange={(e) => handleProfileChange('email', e.target.value)}
                        className={errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                        placeholder="Enter your email address"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="department" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Department
                      </label>
                      <Input
                        id="department"
                        type="text"
                        value={profileData.department}
                        onChange={(e) => handleProfileChange('department', e.target.value)}
                        placeholder="Enter your department"
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSaving || !hasChanges}
                      >
                        {updateLoading && <LoadingSpinner size="sm" />}
                        <span>Update Profile</span>
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {activeTab === 'password' && (
                <div role="tabpanel" id="tabpanel-password" aria-labelledby="tab-password" className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Change Password
                  </h2>
                  
                  <form onSubmit={(e) => { e.preventDefault(); handlePasswordReset(); }} className="space-y-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <span className="text-blue-400">ℹ️</span>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                            Automatic Password Generation
                          </h3>
                          <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
                            <p>
                              A secure password will be generated automatically and displayed to you. 
                              Manual password input is not currently supported.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <span className="text-yellow-400">⚠️</span>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                            Password Reset Warning
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                            <p>
                              A secure password will be generated and displayed to you. Please save it securely.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={passwordLoading}
                      >
                        {passwordLoading && <LoadingSpinner size="sm" />}
                        <span>Generate New Password</span>
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {activeTab === 'account' && (
                <div role="tabpanel" id="tabpanel-account" aria-labelledby="tab-account" className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Account Information
                  </h2>
                  
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          User ID
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white font-mono">
                          {user.id}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Role
                        </label>
                        <RoleBadge role={user.role} />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Organization
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {organizationName}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Status
                        </label>
                        <span className={cn(
                          'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                          user.isActive 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                        )}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Member Since
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {formatDate(user.createdAt)}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Last Updated
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {user.updatedAt ? formatDate(user.updatedAt) : 'Never'}
                        </p>
                      </div>
                    </div>

                    {/* Groups */}
                    {user.groups && user.groups.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Groups ({user.groups.length})
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {user.groups.map((group, index) => {
                            // Handle different group formats safely
                            const groupName = typeof group === 'string' ? 'Unknown' : group.name || 'Unknown';
                            const groupType = typeof group === 'string' ? 'visibility' : group.type || 'visibility';
                            
                            return (
                              <span
                                key={index}
                                className={cn(
                                  'inline-flex px-2 py-1 text-xs font-medium rounded-full',
                                  groupType === 'visibility' 
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                                    : groupType === 'approval'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                )}
                              >
                                {groupName}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Email Accounts - if available */}
                    {user.emailAccounts && user.emailAccounts.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Connected Email Accounts ({user.emailAccounts.length})
                        </label>
                        <div className="space-y-2">
                          {user.emailAccounts.map((account, index) => (
                            <div
                              key={account._id || account.id || index}
                              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md"
                            >
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {account.email}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {account.provider} • Connected {formatDate(account.createdAt)}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className={cn(
                                  'w-2 h-2 rounded-full',
                                  account.isActive ? 'bg-green-400' : 'bg-red-400'
                                )}></span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {account.isActive ? 'Active' : 'Inactive'}
                                </span>
                                {account.needsReauthentication && (
                                  <span className="text-xs text-yellow-600 dark:text-yellow-400">
                                    Needs Auth
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </PageLayout>
    </ProfileErrorBoundary>
  );
};